import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    analytics: 'M18 20V10M12 20V4M6 20v-6',
    chevronLeft: 'M15 18l-6-6 6-6'
};

export default function PassengerAttendanceReport({ clientId, contactEmail, onBack }) {
    const [report, setReport] = useState([]);
    const [loading, setLoading] = useState(true);
    const [contact, setContact] = useState(null);

    useEffect(() => {
        const fetchReport = async () => {
            try {
                // First get client to find contact details
                const cRes = await axios.get(`/api/clients`);
                const client = cRes.data.find(c => c._id === clientId);
                if (!client) throw new Error("Client not found");

                const foundContact = client.contacts?.find(c => c.email === contactEmail);
                if (!foundContact) throw new Error("Contact not found");
                
                setContact(foundContact);

                const payload = {
                    clientId,
                    email: foundContact.email,
                    employeeId: foundContact.employeeId,
                    name: foundContact.firstName ? `${foundContact.firstName} ${foundContact.lastName || ''}`.trim() : foundContact.name
                };

                const res = await fetch(`/api/passenger-report-by-contact`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                const data = await res.json();
                if (data.success) {
                    setReport(data.report || []);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchReport();
    }, [clientId, contactEmail]);

    const passengerName = contact ? (contact.name || contact.firstName || 'Passenger') : 'Loading...';

    return (
        <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto', background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
            <style>{`
                .cd-table { width: 100%; border-collapse: collapse; text-align: left; background: white; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.02); }
                .cd-table th { background: #f8fafc; padding: 16px 20px; font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #f1f5f9; }
                .cd-table td { padding: 16px 20px; font-size: 13px; color: #1e293b; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .cd-table tr:last-child td { border-bottom: none; }
                .cd-table tr:hover td { background: #fbfcfe; }
            `}</style>
            
            <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '8px', border: 'none', background: 'none', color: '#64748b', fontSize: '13px', fontWeight: 700, cursor: 'pointer', transition: '0.2s', marginBottom: '24px' }}>
                <Icon d={icons.chevronLeft} size={16} strokeWidth={2.5} /> Back to Client Details
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: '32px' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #0284c7, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                    <Icon d={icons.analytics} size={20} />
                </div>
                <div>
                    <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#1e293b' }}>{passengerName}'s Attendance</h1>
                    <p style={{ margin: 0, fontSize: 14, color: '#64748b', fontWeight: 600, margin: '6px 0 0 0' }}>Quick-glance date-wise trip report</p>
                </div>
            </div>
            
            <div>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '80px 0' }}>
                        <div style={{ width: 40, height: 40, border: '4px solid #f1f5f9', borderTopColor: '#0284c7', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' }} />
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        <div style={{ fontWeight: 600, color: '#64748b' }}>Loading report...</div>
                    </div>
                ) : report.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '80px 0', background: 'white', borderRadius: 24, border: '1px dashed #cbd5e1' }}>
                        <Icon d={icons.analytics} size={64} color="#cbd5e1" strokeWidth={1} />
                        <div style={{ fontWeight: 700, color: '#475569', marginTop: 16, fontSize: 18 }}>No trip history found.</div>
                        <div style={{ fontSize: 14, color: '#94a3b8', marginTop: 8 }}>This passenger hasn't been allocated to any trips yet.</div>
                    </div>
                ) : (
                    <div style={{ borderRadius: 16, border: '1px solid #f1f5f9', background: 'white' }}>
                        <table className="cd-table">
                            <thead>
                                <tr>
                                    <th style={{ paddingLeft: 24 }}>Date</th>
                                    <th>Trip Name</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.map((item, idx) => (
                                    <tr key={idx}>
                                        <td style={{ paddingLeft: 24, fontWeight: 700, color: '#1e293b' }}>
                                            {item.date ? new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                        </td>
                                        <td style={{ fontWeight: 600, color: '#475569' }}>
                                            {item.tripName}
                                        </td>
                                        <td>
                                            <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: '#f1f5f9', color: '#64748b', fontWeight: 700 }}>
                                                {item.frequency}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ 
                                                fontSize: 12, padding: '6px 12px', borderRadius: 8, fontWeight: 700, display: 'inline-block',
                                                background: item.status === 'Boarded' || item.status === 'Present' ? '#dcfce7' : item.status === 'Absent' ? '#fee2e2' : '#f1f5f9',
                                                color: item.status === 'Boarded' || item.status === 'Present' ? '#15803d' : item.status === 'Absent' ? '#b91c1c' : '#64748b'
                                            }}>
                                                {item.status || 'Pending'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
